<script setup lang="ts">
/**
 * Навигация панели.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ДВА УРОВНЯ, ПОТОМУ ЧТО ДВА СКОУПА
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Верхний блок — то, что существует **над** мероприятиями: список мероприятий
 * и (для администратора) пользователи. Нижний появляется, только когда человек
 * находится **внутри** мероприятия, и содержит его разделы.
 *
 * Разделы мероприятия не показываются вне мероприятия не ради красоты: ссылка
 * «Участники» без выбранного соревнования не имеет адреса — участники всегда
 * чьи-то. Держать её постоянно означало бы либо запоминать «последнее
 * мероприятие» (и однажды открыть чужое), либо вести на пустой экран.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ЧТО СКРЫВАЕТСЯ ПО ПРАВАМ
 * ───────────────────────────────────────────────────────────────────────────
 *
 * • «Пользователи» и «Страны» — только администратору: `UserService` целиком
 *   admin-only, включая чтение, и обычный пользователь получил бы 403 на первом
 *   же запросе.
 * • «Настройки мероприятия» — только тому, кто может писать (`canManage`).
 * • НАБЛЮДАТЕЛЮ И «НАБЛЮДАТЕЛЮ+» внутри мероприятия остаются только
 *   «Участники»: состав, справочники и макет бейджа из меню убраны. Читать их
 *   контракт по-прежнему разрешает — см. ниже, почему это не противоречие.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ⚠️ УБРАННЫЕ ПУНКТЫ НЕ ЗАКРЫТЫ, А ИМЕННО УБРАНЫ ИЗ МЕНЮ
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Наблюдатель имеет право ЧИТАТЬ справочники, состав и макет: так устроен
 * контракт, и `EventGuard` на сервере отдаёт ему их без возражений. Маршруты
 * тоже открыты — адрес, набранный руками, откроет экран, и это правильно.
 *
 * Из меню они убраны потому, что наблюдатель ими не пользуется: его работа —
 * список участников и сканер. Семь пунктов, из которых шесть только для
 * чтения, превращают навигацию в перечень того, чего человеку делать не надо.
 *
 * ⚠️ Отсюда следует, что это НЕ проверка прав и ею подменяться не может: если
 * однажды понадобится закрыть чтение, закрывать придётся гвард маршрута и
 * сервер, а не этот список.
 *
 * ⚠️ Это аффорданс. Скрытый пункт ничего не защищает — адрес можно набрать
 * руками, и тогда откажет гвард, а за ним сервер.
 *
 * Все имена иконок сверены с установленным `@lucide/vue@1.28` — реестр
 * shadcn-vue 2.8 переехал на этот пакет, и часть имён в нём отличается от
 * старых доков `lucide-vue-next` (канонический пример: `FileQuestion`
 * не существует, есть `FileQuestionMark`).
 */
import type { Component } from 'vue'
import {
  CalendarDays,
  ClipboardList,
  Globe,
  IdCard,
  Images,
  KeyRound,
  Layers,
  ListChecks,
  MapPin,
  MonitorSmartphone,
  ScanLine,
  Settings,
  ScanQrCode,
  UserRound,
  Users,
  UsersRound,
} from '@lucide/vue'
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'
import { useAuth } from '@/composables/useAuth'
import { useEventAccess } from '@/composables/useEventAccess'

interface NavItem {
  /** Имя маршрута — ссылки строятся по имени, а не по пути. */
  name: string
  label: string
  icon: Component
  /**
   * Имена маршрутов, при которых пункт подсвечен. Отдельно от `name`, потому
   * что, например, `participant-create` и `participant-edit` — **не** дети
   * `event-participants` (они соседи в дереве маршрутов), и `route.matched`
   * их с «Участниками» не свяжет.
   */
  matches: readonly string[]
}

const route = useRoute()
const { isAdmin } = useAuth()
const { eventId, eventName, canManage, myMemberRole, ready } = useEventAccess()

/**
 * Наблюдатель или «наблюдатель+» — тот, кто мероприятие только читает.
 *
 * ⚠️ ЯВНОЕ ПЕРЕЧИСЛЕНИЕ, А НЕ СРАВНЕНИЕ ПО ПОРЯДКУ. Номера ролей — это провод:
 * `OWNER = 1`, `VIEWER = 2`, `VIEWER_PLUS = 3`. «Наблюдатель+» стоит ПОСЛЕ
 * обычного наблюдателя, хотя прав у него больше, поэтому любое `role <= VIEWER`
 * ошибается — подробный разбор в шапке `useEventAccess`.
 */
const isObserver = computed(
  () => myMemberRole.value === 'MEMBER_ROLE_VIEWER'
    || myMemberRole.value === 'MEMBER_ROLE_VIEWER_PLUS',
)

/**
 * Показывать ли разделы мероприятия сверх «Участников».
 *
 * ⚠️ `canManage` СТОИТ ПЕРВЫМ И ОТДЕЛЬНО, потому что администратор может быть
 * ещё и вписан в состав наблюдателем. Тогда `myMemberRole` честно вернёт
 * `VIEWER`, и одна проверка роли спрятала бы разделы от того, кто ими
 * распоряжается.
 *
 * ⚠️ `ready` — не косметика, и направление здесь выбрано. Пока состав не
 * загружен, роль ещё неизвестна, и меню показывает МЕНЬШИЙ набор: так оно
 * только РАСТЁТ по мере ответа — ровно как «Настройки», появляющиеся вместе с
 * `canManage`. Обратный порядок означал бы, что у наблюдателя на каждом входе
 * в мероприятие семь пунктов схлопываются в один, и это читается как сбой, а
 * не как загрузка.
 */
const showAllEventSections = computed(
  () => canManage.value || (ready.value && !isObserver.value),
)

const activeName = computed(() => (typeof route.name === 'string' ? route.name : ''))

function isActive(item: NavItem): boolean {
  return item.matches.includes(activeName.value)
}

/** Разделы над мероприятиями. */
const globalItems = computed<NavItem[]>(() => {
  const items: NavItem[] = [
    {
      name: 'events',
      label: 'Мероприятия',
      icon: CalendarDays,
      matches: ['events', 'event-create'],
    },
  ]

  if (isAdmin.value) {
    items.push({
      name: 'users',
      label: 'Пользователи',
      icon: Users,
      matches: ['users', 'user-create', 'user-edit'],
    })
    // Страны стоят в ВЕРХНЕЙ группе, а не в разделах мероприятия, и это не
    // вопрос вкуса: справочник один на всю систему, и правка в нём видна сразу
    // всем соревнованиям. Пункт внутри мероприятия обещал бы обратное.
    items.push({
      name: 'countries',
      label: 'Страны',
      icon: Globe,
      matches: ['countries'],
    })
  }

  return items
})

/**
 * Разделы текущего мероприятия. Пустой массив, когда мероприятие не выбрано, —
 * шаблон по нему и решает, рисовать ли вторую группу.
 */
const eventItems = computed<NavItem[]>(() => {
  if (!eventId.value) return []

  // «Участники» — единственный пункт, который видят все, включая наблюдателя:
  // это его рабочий экран. Остальное ниже добавляется только тем, кто
  // мероприятие ведёт.
  const items: NavItem[] = [
    {
      name: 'event-participants',
      label: 'Участники',
      icon: ClipboardList,
      matches: [
        'event-participants',
        'participant-create',
        'participant-edit',
        'participant-import',
      ],
    },
  ]

  if (!showAllEventSections.value) return items

  items.push(
    {
      name: 'event-members',
      label: 'Состав',
      icon: UsersRound,
      matches: ['event-members'],
    },
    {
      // Дополнительные поля живут ВНУТРИ этого экрана: поле привязано к роли, и
      // отдельный пункт «Доп. поля» задавал вопрос «кого это касается?», на
      // который плоский список ответить не мог. Не возвращать разделением.
      name: 'event-participant-roles',
      label: 'Роли и поля',
      icon: ListChecks,
      // Ссылка ведёт на СПИСОК, а страницы создания и правки роли лишь
      // подсвечивают пункт: они соседи в дереве маршрутов, а не дети списка,
      // и `route.matched` их сюда не приведёт.
      matches: [
        'event-participant-roles',
        'event-participant-role-create',
        'event-participant-role-edit',
      ],
    },
    {
      // ⚠️ «Доступ» здесь про ПЛОЩАДКУ (раздевалки, разминка, микст-зона), а не
      // про права в системе. Пункт стоит рядом с ролями намеренно: зоны потом
      // привязываются к ним, и искать их человек будет здесь.
      name: 'event-access-zones',
      label: 'Зоны доступа',
      icon: MapPin,
      matches: ['event-access-zones'],
    },
    {
      // ⚠️ Второй справочник рядом с зонами, а не их замена: зона говорит,
      // КУДА пускают, уровень — НАСКОЛЬКО глубоко (цифра на бейдже). И «доступ»
      // здесь по-прежнему не про права в системе.
      //
      // `Layers` — слои, а не ступени лестницы: иконка обязана читаться как
      // «глубина», а не как «ранг». Имя сверено с `@lucide/vue@1.28`.
      name: 'event-access-levels',
      label: 'Уровни доступа',
      icon: Layers,
      matches: ['event-access-levels'],
    },
    {
      // ⚠️ Третий справочник рядом с зонами и уровнями, а не их замена: зона —
      // «куда», уровень — «насколько глубоко», пиктограмма — «что положено»
      // (картинка на бейдже). Прав в системе по-прежнему не даёт ни одна из
      // трёх.
      //
      // `Images` — стопка картинок: пункт про КАРТИНКИ, а не про «галерею» и не
      // про «медиа». Имя сверено с `@lucide/vue@1.28`.
      name: 'event-pictograms',
      label: 'Пиктограммы',
      icon: Images,
      matches: ['event-pictograms'],
    },
    {
      // ⚠️ Из МЕНЮ наблюдателя убран вместе с остальными разделами, но маршрут
      // ему открыт: макет читают, чтобы печатать, а печатает как раз оператор
      // аккредитации. Печать при этом запускается из списка участников, куда
      // он и ходит, — отдельный пункт нужен тому, кто макет ВЕРСТАЕТ.
      // Кнопки правки внутри закрыты `canManage`.
      name: 'event-badge',
      label: 'Бейдж',
      icon: IdCard,
      matches: ['event-badge'],
    },
  )

  if (canManage.value) {
    items.push({
      name: 'event-settings',
      label: 'Настройки',
      icon: Settings,
      matches: ['event-settings'],
    })
  }

  return items
})

/**
 * Ссылка в сканер — отдельно от списка выше, потому что ведёт в ДРУГОЙ каркас
 * (`ScannerLayout`, без сайдбара). Пункт, уводящий из текущей навигации, не
 * должен стоять в одном ряду с теми, что её сохраняют.
 *
 * Ведёт сразу в текущее мероприятие, а не на экран выбора: человек уже в нём.
 *
 * Доступен всем участникам: сканирование требует лишь доступа на чтение, и
 * наблюдатель — как раз тот, для кого сканер сделан.
 */
const scannerActive = computed(() => activeName.value.startsWith('scanner'))

const accountItems: readonly NavItem[] = [
  { name: 'profile', label: 'Мой профиль', icon: UserRound, matches: ['profile'] },
  { name: 'change-password', label: 'Смена пароля', icon: KeyRound, matches: ['change-password'] },
  { name: 'sessions', label: 'Активные сессии', icon: MonitorSmartphone, matches: ['sessions'] },
]

/** Параметры для ссылок внутри мероприятия — все они требуют `:eventId`. */
const eventParams = computed(() => ({ eventId: eventId.value ?? '' }))
</script>

<template>
  <!--
    `collapsible="icon"` — сворачивание в полоску иконок (⌘/Ctrl+B или кнопка
    в шапке), а не полное исчезновение: на узком ноутбуке место нужно, но
    терять навигацию целиком незачем. На мобильном `Sidebar` сам подменяется
    на `Sheet`.
  -->
  <Sidebar collapsible="icon">
    <SidebarHeader>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton as-child size="lg">
            <RouterLink :to="{ name: 'events' }">
              <div class="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <ScanLine class="size-4" />
              </div>
              <div class="grid flex-1 text-left text-sm leading-tight">
                <span class="truncate font-semibold">Аккредитация</span>
                <span class="truncate text-xs text-muted-foreground">Панель организатора</span>
              </div>
            </RouterLink>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarHeader>

    <SidebarContent>
      <SidebarGroup>
        <SidebarGroupLabel>Общее</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem v-for="item in globalItems" :key="item.name">
              <!--
                `as-child` + `RouterLink`: кнопка отдаёт свои классы настоящему
                `<a href>`. Обычный `@click="router.push(...)"` лишил бы ссылку
                middle-click, «открыть в новой вкладке» и предпросмотра адреса
                в статусной строке.
              -->
              <SidebarMenuButton as-child :is-active="isActive(item)" :tooltip="item.label">
                <RouterLink :to="{ name: item.name }">
                  <component :is="item.icon" />
                  <span>{{ item.label }}</span>
                </RouterLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      <!-- Вторая группа существует, только пока человек внутри мероприятия. -->
      <SidebarGroup v-if="eventItems.length > 0">
        <!--
          Подпись группы — название мероприятия, а не слово «Мероприятие»:
          в двух вкладках с разными соревнованиями это единственное, что их
          различает без чтения адресной строки.
        -->
        <SidebarGroupLabel class="truncate">
          {{ eventName || 'Мероприятие' }}
        </SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem v-for="item in eventItems" :key="item.name">
              <SidebarMenuButton as-child :is-active="isActive(item)" :tooltip="item.label">
                <RouterLink :to="{ name: item.name, params: eventParams }">
                  <component :is="item.icon" />
                  <span>{{ item.label }}</span>
                </RouterLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      <!--
        Переход в сканер: другой каркас, поэтому отдельной группой, а не
        пунктом в списке разделов мероприятия.
      -->
      <SidebarGroup v-if="eventId">
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton as-child :is-active="scannerActive" tooltip="Сканер">
                <RouterLink :to="{ name: 'scanner-event', params: eventParams }">
                  <ScanQrCode />
                  <span>Сканировать вход</span>
                </RouterLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      <SidebarGroup>
        <SidebarGroupLabel>Аккаунт</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem v-for="item in accountItems" :key="item.name">
              <SidebarMenuButton as-child :is-active="isActive(item)" :tooltip="item.label">
                <RouterLink :to="{ name: item.name }">
                  <component :is="item.icon" />
                  <span>{{ item.label }}</span>
                </RouterLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </SidebarContent>

    <SidebarFooter>
      <!-- В свёрнутом виде подпись прячется, чтобы не рвать полоску иконок. -->
      <div class="px-2 py-1 text-xs text-muted-foreground group-data-[collapsible=icon]:hidden">
        Аккредитация · панель организатора
      </div>
    </SidebarFooter>
  </Sidebar>
</template>
